import { metersBetween, ringAt, type LatLng } from './geometry'
import { detectionProfileSchema, type DetectionProfile } from './profile'
import { allRulesOn, applyRules, type EngineRules } from './rules'
import type {
  ActiveVisit,
  CaptureSource,
  DetectionSite,
  EngineEvent,
  EngineInput,
  EngineState,
  EngineView,
  Hit,
  NotAdmissibleReason,
  Observation,
  PublicStatus,
  StepResult,
  SuspendReason,
  VisitTrigger,
} from './types'

// Moteur de détection « drapeau + zone de travail ». Fonction pure : aucune dépendance au DOM, à Google Maps, au
// réseau ni à Capacitor; l'heure arrive dans chaque entrée. Mêmes entrées → mêmes sorties (simulateur et GPS réel).
//
// Règles (spécification du 11 oct. 2026, §5) :
// - une seule résidence active; elle seule reçoit du temps, jamais transféré;
// - capture = plusieurs positions fiables consécutives dans le rayon de capture, sur une durée minimale, après une
//   entrée (une résidence dans laquelle on se trouve déjà exige d'en sortir puis d'y revenir);
// - deux adresses plausibles → choix explicite, la tâche active est conservée;
// - temps crédité seulement entre deux positions fiables de la zone de travail, jamais à travers un trou;
// - bande entre travail et sortie : verrou conservé, ajout de temps suspendu;
// - fin de visite : entrée confirmée dans la suivante ou sortie confirmée au-delà de la distance de sortie;
//   terminée seulement si admissible (durée minimale et part de mouvement), sinon retour « à faire »;
// - GPS imprécis, périmé, saut incohérent ou perte de signal : aucune décision, aucun temps ajouté;
// - une résidence terminée ou en problème n'est jamais capturée; seules des actions explicites la modifient.

export const ENGINE_VERSION = '0.1.0'

/** Recul d'horloge toléré entre deux entrées avant de conclure à une anomalie. */
const clockToleranceMs = 2_000

export function createEngine(
  profile: DetectionProfile,
  sites: readonly DetectionSite[],
  rules: EngineRules = allRulesOn,
): EngineState {
  const checked = applyRules(detectionProfileSchema.parse(profile), rules)
  const ids = new Set<string>()
  for (const site of sites) {
    if (ids.has(site.itemId)) throw new Error(`Résidence en double : ${site.itemId}`)
    ids.add(site.itemId)
  }
  return {
    engineVersion: ENGINE_VERSION,
    profile: checked,
    rules: { ...rules },
    sites: sites.map((site) => ({
      itemId: site.itemId,
      order: site.order,
      flag: site.flag ? { lat: site.flag.lat, lng: site.flag.lng } : null,
      status: site.status,
    })),
    initialised: false,
    paused: false,
    gps: { status: 'UNKNOWN', reason: null, since: null },
    lastNow: null,
    lastObservationAt: null,
    lastGood: null,
    pendingJump: null,
    hits: {},
    disarmed: [],
    active: null,
    ambiguity: null,
  }
}

/**
 * Change le profil ou les interrupteurs en cours de mission (calibration sur le terrain, TEMPORAIRE). La visite
 * active, le temps acquis et l'état GPS sont conservés; les séquences de capture repartent de zéro.
 */
export function reconfigure(previous: EngineState, profile: DetectionProfile, rules: EngineRules): EngineState {
  const state = JSON.parse(JSON.stringify(previous)) as EngineState
  state.profile = applyRules(detectionProfileSchema.parse(profile), rules)
  state.rules = { ...rules }
  state.hits = {}
  if (!rules.arming) state.disarmed = []
  return state
}

interface Context {
  state: EngineState
  profile: DetectionProfile
  events: EngineEvent[]
  now: number
}

export function step(previous: EngineState, input: EngineInput): StepResult {
  const state = JSON.parse(JSON.stringify(previous)) as EngineState
  const context: Context = { state, profile: state.profile, events: [], now: input.now }

  if (state.lastNow !== null && input.now < state.lastNow - clockToleranceMs) {
    suspend(context, 'CLOCK_ANOMALY', [])
    state.lastObservationAt = null
    state.lastGood = null
    state.pendingJump = null
  }
  state.lastNow = input.now

  switch (input.kind) {
    case 'OBSERVATION':
      observe(context, input.observation)
      break
    case 'TICK':
      tick(context)
      break
    case 'CHOOSE_SITE':
      chooseSite(context, input.itemId)
      break
    case 'CHOOSE_NONE':
      chooseNone(context)
      break
    case 'REPORT_PROBLEM':
      reportProblem(context, input.itemId)
      break
    case 'COMPLETE_MANUALLY':
      completeManually(context, input.itemId, input.reason)
      break
    case 'REOPEN':
      reopen(context, input.itemId, input.reason)
      break
    case 'PAUSE':
      pause(context)
      break
    case 'RESUME':
      resume(context)
      break
  }
  return { state, events: context.events }
}

/** Rejoue une suite d'entrées; utilisé par les tests, le laboratoire et la restauration. */
export function replay(initial: EngineState, inputs: readonly EngineInput[]): StepResult {
  let state = initial
  const events: EngineEvent[] = []
  for (const input of inputs) {
    const result = step(state, input)
    state = result.state
    events.push(...result.events)
  }
  return { state, events }
}

// ——— Observations ———

function observe(context: Context, observation: Observation) {
  const { state } = context
  if (state.paused) return
  if (state.lastObservationAt !== null && observation.at <= state.lastObservationAt) {
    context.events.push({
      kind: 'OBSERVATION_IGNORED',
      at: context.now,
      reason: 'OUT_OF_ORDER',
      observationIds: [observation.id],
    })
    return
  }
  state.lastObservationAt = observation.at

  const point: LatLng = { lat: observation.lat, lng: observation.lng }
  const problem = assess(context, observation, point)
  if (problem) {
    suspend(context, problem, [observation.id])
    return
  }

  if (state.gps.status === 'SUSPENDED') {
    context.events.push({
      kind: 'DETECTION_RESUMED',
      at: context.now,
      suspendedMs: state.gps.since === null ? 0 : context.now - state.gps.since,
      observationIds: [observation.id],
    })
  }
  state.gps = { status: 'OK', reason: null, since: context.now }
  state.lastGood = { id: observation.id, at: observation.at, point }

  updateArming(context, point)

  if (state.active) trackActive(context, observation, point)
  if (state.ambiguity) {
    trackAmbiguity(context, observation, point)
    return
  }
  detectCapture(context, observation, point)
}

function assess(context: Context, observation: Observation, point: LatLng): SuspendReason | null {
  const { state, profile } = context
  const ageMs = context.now - observation.at
  if (ageMs > profile.maxFixAgeS * 1000 || ageMs < -profile.maxFixAgeS * 1000) return 'STALE'
  if (observation.accuracyM === null ? state.rules.precision : observation.accuracyM > profile.maxAccuracyM) {
    return 'INACCURATE'
  }

  const reference = state.lastGood
  if (reference && isJump(profile, reference.at, reference.point, observation, point)) {
    // Deux positions successives cohérentes entre elles après un saut : le saut était réel, nouvelle référence.
    const pending = state.pendingJump
    if (pending && !isJump(profile, pending.at, pending.point, observation, point)) {
      state.pendingJump = null
      if (state.active) {
        state.active.creditAnchor = null
        state.active.recent = []
      }
      return null
    }
    state.pendingJump = { id: observation.id, at: observation.at, point }
    return 'JUMP'
  }
  state.pendingJump = null
  return null
}

function isJump(profile: DetectionProfile, fromAt: number, from: LatLng, observation: Observation, to: LatLng) {
  const seconds = Math.max((observation.at - fromAt) / 1000, 0.001)
  const allowed = profile.maxSpeedMps * seconds + (observation.accuracyM ?? 0)
  return metersBetween(from, to) > allowed
}

function tick(context: Context) {
  const { state, profile } = context
  if (state.paused || state.gps.status !== 'OK' || !state.lastGood) return
  if (context.now - state.lastGood.at > profile.maxFixAgeS * 1000) suspend(context, 'SIGNAL_LOST', [])
}

function suspend(context: Context, reason: SuspendReason, observationIds: string[]) {
  const { state } = context
  if (state.gps.status !== 'SUSPENDED') {
    state.gps = { status: 'SUSPENDED', reason, since: context.now }
    context.events.push({ kind: 'DETECTION_SUSPENDED', at: context.now, reason, observationIds })
  }
  // Rien n'est décidé ni crédité à travers la période aveugle : séquences remises à zéro, temps acquis conservé.
  clearHits(context)
  if (state.active) {
    state.active.creditAnchor = null
    state.active.recent = []
    state.active.exitHits = []
  }
  if (state.ambiguity) state.ambiguity.leaveHits = []
}

// ——— Armement : une capture exige une entrée ———

function updateArming(context: Context, point: LatLng) {
  const { state, profile } = context
  if (!state.rules.arming) {
    state.initialised = true
    state.disarmed = []
    return
  }
  if (!state.initialised) {
    // Au premier relevé, une résidence déjà sous le véhicule n'est pas capturée par simple immobilité.
    state.initialised = true
    state.disarmed = eligibleSites(state)
      .filter((site) => site.flag && metersBetween(site.flag, point) <= profile.captureRadiusM)
      .map((site) => site.itemId)
    return
  }
  state.disarmed = state.disarmed.filter((itemId) => {
    const site = findSite(state, itemId)
    return site?.flag ? metersBetween(site.flag, point) <= profile.captureRadiusM : false
  })
}

function disarmInside(context: Context, itemIds: readonly string[]) {
  const { state, profile } = context
  if (!state.rules.arming) return
  const point = state.lastGood?.point
  for (const itemId of itemIds) {
    const site = findSite(state, itemId)
    if (!site?.flag || state.disarmed.includes(itemId)) continue
    if (!point || metersBetween(site.flag, point) <= profile.captureRadiusM) state.disarmed.push(itemId)
  }
}

// ——— Résidence active ———

function trackActive(context: Context, observation: Observation, point: LatLng) {
  const { state, profile } = context
  const active = state.active
  if (!active) return
  const flag = findSite(state, active.itemId)?.flag
  if (!flag) return

  const ring = ringAt(metersBetween(flag, point), profile)
  active.ring = ring
  const sample = { at: observation.at, point }
  active.recent = [...active.recent, sample].filter(
    (entry) => entry.at >= observation.at - profile.movementWindowS * 1000,
  )

  if (ring === 'CAPTURE' || ring === 'WORK') {
    const anchor = active.creditAnchor
    if (anchor && observation.at - anchor.at <= profile.maxGapS * 1000) {
      const interval = observation.at - anchor.at
      active.observedMs += interval
      if (isMoving(profile, active, point, observation.accuracyM ?? 0)) active.movingMs += interval
    }
    active.creditAnchor = sample
    active.exitHits = []
    return
  }

  active.creditAnchor = null
  if (ring === 'BAND') {
    // Bande entre travail et sortie : verrou conservé, la sortie candidate repart de zéro (pas d'oscillation).
    active.exitHits = []
    return
  }
  active.exitHits.push({ observationId: observation.id, at: observation.at })
  if (state.ambiguity) return
  if (confirmed(active.exitHits, profile.exitConfirmCount, profile.exitConfirmWindowS)) {
    endVisit(context, 'DEPARTURE', active.exitHits.map((hit) => hit.observationId))
  }
}

function isMoving(profile: DetectionProfile, active: ActiveVisit, point: LatLng, accuracyM: number) {
  const threshold = Math.max(profile.stationaryJitterM, accuracyM)
  return active.recent.some((entry) => metersBetween(entry.point, point) > threshold)
}

function admissibility(profile: DetectionProfile, active: ActiveVisit): NotAdmissibleReason[] {
  const reasons: NotAdmissibleReason[] = []
  if (active.observedMs < profile.minInterventionS * 1000) reasons.push('TOO_SHORT')
  if (profile.minMovingShare > 0 && (active.observedMs === 0 || active.movingMs / active.observedMs < profile.minMovingShare)) {
    reasons.push('STATIONARY')
  }
  return reasons
}

/** Termine la visite active si elle est admissible; sinon la résidence revient « à faire » avec la trace. */
function endVisit(context: Context, trigger: VisitTrigger, observationIds: string[]) {
  const { state, profile } = context
  const active = state.active
  if (!active) return
  const site = findSite(state, active.itemId)
  const measures = { observedMs: active.observedMs, movingMs: active.movingMs }
  const reasons = admissibility(profile, active)
  if (site?.status === 'TODO' && reasons.length === 0) {
    site.status = 'DONE'
    context.events.push({
      kind: 'COMPLETED',
      at: context.now,
      itemId: active.itemId,
      source: 'AUTO',
      trigger,
      observationIds,
      ...measures,
    })
  } else {
    context.events.push({
      kind: 'VISIT_NOT_ADMISSIBLE',
      at: context.now,
      itemId: active.itemId,
      trigger,
      reasons,
      observationIds,
      ...measures,
    })
  }
  state.active = null
  // Un nouveau passage sera une nouvelle visite : il faut d'abord sortir du drapeau.
  disarmInside(context, [active.itemId])
}

// ——— Capture ———

function detectCapture(context: Context, observation: Observation, point: LatLng) {
  const { state, profile } = context
  const ready: string[] = []
  const plausible = new Set<string>()
  const plausibleRadius = profile.captureRadiusM + (observation.accuracyM ?? 0)

  for (const site of eligibleSites(state)) {
    if (!site.flag) continue
    const distance = metersBetween(site.flag, point)
    if (distance <= plausibleRadius) plausible.add(site.itemId)
    const hits = state.hits[site.itemId] ?? []
    if (distance > profile.captureRadiusM || state.disarmed.includes(site.itemId)) {
      if (hits.length > 0) loseCandidate(context, site.itemId, [observation.id])
      continue
    }
    if (hits.length === 0) {
      context.events.push({
        kind: 'CANDIDATE',
        at: context.now,
        itemId: site.itemId,
        distanceM: Math.round(distance * 10) / 10,
        observationIds: [observation.id],
      })
    }
    const next = [...hits, { observationId: observation.id, at: observation.at }]
    state.hits[site.itemId] = next
    if (confirmed(next, profile.captureConfirmCount, profile.captureConfirmWindowS)) ready.push(site.itemId)
  }
  if (ready.length === 0) return
  // Encore plausible à l'entrée de la résidence active (entrée commune, manœuvre) : pas de transfert.
  const activeFlag = state.active ? findSite(state, state.active.itemId)?.flag : null
  if (activeFlag && metersBetween(activeFlag, point) <= plausibleRadius) return

  const involved = new Set([...ready, ...plausible, ...Object.keys(state.hits)])
  const observationIds = uniqueObservationIds(ready.flatMap((itemId) => state.hits[itemId] ?? []))
  if (involved.size > 1 && !state.rules.ambiguity) {
    // Ambiguïté coupée (calibration) : le drapeau prêt le plus proche l'emporte.
    const nearest = ready
      .flatMap((itemId) => {
        const flag = findSite(state, itemId)?.flag
        return flag ? [{ itemId, distance: metersBetween(flag, point) }] : []
      })
      .sort((a, b) => a.distance - b.distance)[0]
    if (nearest) capture(context, nearest.itemId, 'AUTO', observationIds, { at: observation.at, point })
    return
  }
  if (involved.size > 1) {
    const itemIds = sortByOrder(state, [...involved])
    state.ambiguity = { itemIds, since: context.now, observationIds, leaveHits: [] }
    state.hits = {}
    context.events.push({ kind: 'AMBIGUITY', at: context.now, itemIds, observationIds })
    return
  }
  const [itemId] = ready
  if (itemId) capture(context, itemId, 'AUTO', observationIds, { at: observation.at, point })
}

function capture(
  context: Context,
  itemId: string,
  source: CaptureSource,
  observationIds: string[],
  anchor: { at: number; point: LatLng } | null,
) {
  const { state, profile } = context
  // Transition atomique : la visite précédente est close (terminée ou rendue « à faire ») avant l'activation.
  if (state.active) endVisit(context, 'NEXT_ENTRY', observationIds)
  state.hits = {}
  const flag = findSite(state, itemId)?.flag
  const point = anchor?.point ?? state.lastGood?.point
  state.active = {
    itemId,
    capturedAt: context.now,
    source,
    ring: flag && point ? ringAt(metersBetween(flag, point), profile) : 'CAPTURE',
    creditAnchor: anchor,
    recent: anchor ? [anchor] : [],
    exitHits: [],
    captureObservationIds: observationIds,
    observedMs: 0,
    movingMs: 0,
  }
  context.events.push({ kind: 'CAPTURED', at: context.now, itemId, source, observationIds })
}

function trackAmbiguity(context: Context, observation: Observation, point: LatLng) {
  const { state, profile } = context
  const ambiguity = state.ambiguity
  if (!ambiguity) return
  const away = ambiguity.itemIds.every((itemId) => {
    const flag = findSite(state, itemId)?.flag
    return !flag || metersBetween(flag, point) > profile.exitDistanceM
  })
  if (!away) {
    ambiguity.leaveHits = []
    return
  }
  ambiguity.leaveHits.push({ observationId: observation.id, at: observation.at })
  if (!confirmed(ambiguity.leaveHits, profile.exitConfirmCount, profile.exitConfirmWindowS)) return
  state.ambiguity = null
  context.events.push({
    kind: 'AMBIGUITY_CLEARED',
    at: context.now,
    itemIds: ambiguity.itemIds,
    reason: 'LEFT_AREA',
  })
  // La visite active, gelée pendant l'ambiguïté, est évaluée sur la même sortie confirmée.
  const active = state.active
  if (active && confirmed(active.exitHits, profile.exitConfirmCount, profile.exitConfirmWindowS)) {
    endVisit(context, 'DEPARTURE', active.exitHits.map((hit) => hit.observationId))
  }
}

function loseCandidate(context: Context, itemId: string, observationIds: string[]) {
  const { state } = context
  state.hits = Object.fromEntries(Object.entries(state.hits).filter(([key]) => key !== itemId))
  context.events.push({ kind: 'CANDIDATE_LOST', at: context.now, itemId, observationIds })
}

function clearHits(context: Context) {
  for (const itemId of Object.keys(context.state.hits)) loseCandidate(context, itemId, [])
}

// ——— Actions explicites ———

function refuse(context: Context, action: EngineInput['kind'], reason: string) {
  context.events.push({ kind: 'ACTION_REFUSED', at: context.now, action, reason })
}

function chooseSite(context: Context, itemId: string) {
  const { state } = context
  const ambiguity = state.ambiguity
  if (!ambiguity) {
    refuse(context, 'CHOOSE_SITE', 'NO_AMBIGUITY')
    return
  }
  if (!ambiguity.itemIds.includes(itemId)) {
    refuse(context, 'CHOOSE_SITE', 'NOT_A_CANDIDATE')
    return
  }
  if (findSite(state, itemId)?.status !== 'TODO') {
    refuse(context, 'CHOOSE_SITE', 'NOT_TODO')
    return
  }
  state.ambiguity = null
  context.events.push({
    kind: 'AMBIGUITY_CLEARED',
    at: context.now,
    itemIds: ambiguity.itemIds,
    reason: 'SITE_CHOSEN',
  })
  // Le choix confirme l'identité, pas le travail : le temps part de zéro, au prochain relevé fiable.
  capture(context, itemId, 'MANUAL_CHOICE', ambiguity.observationIds, null)
  disarmInside(
    context,
    ambiguity.itemIds.filter((candidate) => candidate !== itemId),
  )
}

function chooseNone(context: Context) {
  const { state } = context
  const ambiguity = state.ambiguity
  if (!ambiguity) {
    refuse(context, 'CHOOSE_NONE', 'NO_AMBIGUITY')
    return
  }
  state.ambiguity = null
  context.events.push({
    kind: 'AMBIGUITY_CLEARED',
    at: context.now,
    itemIds: ambiguity.itemIds,
    reason: 'NONE_CHOSEN',
  })
  // « Je passe seulement » : aucune de ces adresses n'est redemandée tant que le véhicule n'en est pas sorti.
  disarmInside(context, ambiguity.itemIds)
}

function reportProblem(context: Context, itemId: string) {
  const { state } = context
  const site = findSite(state, itemId)
  if (!site) {
    refuse(context, 'REPORT_PROBLEM', 'UNKNOWN_SITE')
    return
  }
  if (site.status === 'DONE') {
    refuse(context, 'REPORT_PROBLEM', 'ALREADY_DONE')
    return
  }
  if (site.status === 'PROBLEM') return
  site.status = 'PROBLEM'
  const active = state.active?.itemId === itemId ? state.active : null
  context.events.push({
    kind: 'PROBLEM_REPORTED',
    at: context.now,
    itemId,
    ...(active ? { observedMs: active.observedMs, movingMs: active.movingMs } : {}),
  })
  if (active) state.active = null
  dropFromCapture(context, itemId)
}

function completeManually(context: Context, itemId: string, reason: string) {
  const { state } = context
  const site = findSite(state, itemId)
  if (!site) {
    refuse(context, 'COMPLETE_MANUALLY', 'UNKNOWN_SITE')
    return
  }
  if (site.status === 'DONE') {
    refuse(context, 'COMPLETE_MANUALLY', 'ALREADY_DONE')
    return
  }
  if (reason.trim() === '') {
    refuse(context, 'COMPLETE_MANUALLY', 'REASON_REQUIRED')
    return
  }
  const previousStatus = site.status
  site.status = 'DONE'
  const active = state.active?.itemId === itemId ? state.active : null
  context.events.push({
    kind: 'COMPLETED',
    at: context.now,
    itemId,
    source: 'MANUAL',
    reason: reason.trim(),
    previousStatus,
    ...(active ? { observedMs: active.observedMs, movingMs: active.movingMs } : {}),
  })
  if (active) state.active = null
  dropFromCapture(context, itemId)
  // Les drapeaux sous le véhicule exigent une nouvelle entrée : pas de démarrage par simple immobilité.
  disarmInside(
    context,
    eligibleSites(state).map((candidate) => candidate.itemId),
  )
}

function reopen(context: Context, itemId: string, reason: string) {
  const { state } = context
  const site = findSite(state, itemId)
  if (!site) {
    refuse(context, 'REOPEN', 'UNKNOWN_SITE')
    return
  }
  if (site.status !== 'DONE') {
    refuse(context, 'REOPEN', 'NOT_DONE')
    return
  }
  if (reason.trim() === '') {
    refuse(context, 'REOPEN', 'REASON_REQUIRED')
    return
  }
  site.status = 'TODO'
  context.events.push({ kind: 'REOPENED', at: context.now, itemId, reason: reason.trim() })
  disarmInside(context, [itemId])
}

function pause(context: Context) {
  const { state } = context
  if (state.paused) return
  state.paused = true
  clearHits(context)
  if (state.active) {
    state.active.creditAnchor = null
    state.active.recent = []
    state.active.exitHits = []
  }
  if (state.ambiguity) state.ambiguity.leaveHits = []
  context.events.push({ kind: 'PAUSED', at: context.now })
}

function resume(context: Context) {
  if (!context.state.paused) return
  context.state.paused = false
  context.events.push({ kind: 'RESUMED', at: context.now })
}

function dropFromCapture(context: Context, itemId: string) {
  const { state } = context
  if (state.hits[itemId]) loseCandidate(context, itemId, [])
  if (state.ambiguity?.itemIds.includes(itemId)) {
    const remaining = state.ambiguity.itemIds.filter((candidate) => candidate !== itemId)
    if (remaining.length > 1) {
      state.ambiguity.itemIds = remaining
    } else {
      context.events.push({
        kind: 'AMBIGUITY_CLEARED',
        at: context.now,
        itemIds: state.ambiguity.itemIds,
        reason: 'NONE_CHOSEN',
      })
      disarmInside(context, state.ambiguity.itemIds)
      state.ambiguity = null
    }
  }
}

// ——— Lecture ———

export function view(state: EngineState): EngineView {
  const statuses: Record<string, PublicStatus> = {}
  for (const site of state.sites) {
    if (site.status === 'DONE') statuses[site.itemId] = 'DONE'
    else if (site.status === 'PROBLEM') statuses[site.itemId] = 'PROBLEM'
    else if (state.active?.itemId === site.itemId) statuses[site.itemId] = 'IN_PROGRESS'
    else if ((state.hits[site.itemId]?.length ?? 0) > 0 || state.ambiguity?.itemIds.includes(site.itemId)) {
      statuses[site.itemId] = 'APPROACH'
    } else statuses[site.itemId] = 'TODO'
  }
  const active = state.active
  return {
    gps: state.gps,
    paused: state.paused,
    active: active
      ? {
          itemId: active.itemId,
          ring: active.ring,
          source: active.source,
          capturedAt: active.capturedAt,
          observedMs: active.observedMs,
          movingMs: active.movingMs,
          exitCandidate: active.exitHits.length > 0,
          admissible: admissibility(state.profile, active).length === 0,
        }
      : null,
    ambiguity: state.ambiguity ? [...state.ambiguity.itemIds] : null,
    statuses,
    sitesWithoutFlag: state.sites.filter((site) => !site.flag).map((site) => site.itemId),
  }
}

export interface EngineInternals {
  /** Positions consécutives dans le rayon de capture, par résidence. */
  hits: Record<string, number>
  /** Résidences qui attendent une sortie du rayon de capture avant toute capture. */
  disarmed: string[]
  initialised: boolean
}

/** Compteurs internes pour le diagnostic de calibration (temporaire, jamais une décision). */
export function internals(state: EngineState): EngineInternals {
  return {
    hits: Object.fromEntries(Object.entries(state.hits).map(([itemId, hits]) => [itemId, hits.length])),
    disarmed: [...state.disarmed],
    initialised: state.initialised,
  }
}

// ——— Utilitaires ———

function findSite(state: EngineState, itemId: string): DetectionSite | undefined {
  return state.sites.find((site) => site.itemId === itemId)
}

function eligibleSites(state: EngineState): DetectionSite[] {
  return state.sites.filter(
    (site) => site.flag !== null && site.status === 'TODO' && site.itemId !== state.active?.itemId,
  )
}

function confirmed(hits: readonly Hit[], count: number, windowS: number): boolean {
  const first = hits[0]
  const last = hits[hits.length - 1]
  if (!first || !last || hits.length < count) return false
  return last.at - first.at >= windowS * 1000
}

function uniqueObservationIds(hits: readonly Hit[]): string[] {
  return [...new Set(hits.map((hit) => hit.observationId))]
}

function sortByOrder(state: EngineState, itemIds: string[]): string[] {
  const order = (itemId: string) => findSite(state, itemId)?.order ?? Number.MAX_SAFE_INTEGER
  return [...itemIds].sort((a, b) => order(a) - order(b))
}
